/**
 * TaxControl — VAT settings screen.
 *   • Auto-pulls TRN from company documents (tax_certificate)
 *   • Editable tax percentage (default UAE VAT = 5%)
 *   • Toggle to enable/disable tax application (affects invoice creation)
 *
 * Reads/writes: /api/admin/tax-settings
 *   PATCH        → update tax_percent / enabled / notes / trn_number
 *   POST /sync-trn → re-pull TRN from company_documents.tax_certificate
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, Save, ToggleLeft, ToggleRight, RotateCw, Info,
} from 'lucide-react';

import { useLanguage } from '../../context/LanguageContext';
import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { getAuthHeaders } from '../../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TaxControl() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [draftPercent, setDraftPercent] = useState('');
  const [draftNotes, setDraftNotes] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/tax-settings`, { headers: getAuthHeaders() });
      const d = r.data.data || {};
      setSettings(d);
      setDraftPercent(String(d.tax_percent ?? ''));
      setDraftNotes(d.notes || '');
    } catch {
      toast.error(t('فشل التحميل', 'Load failed'));
    } finally {
      setLoading(false);
    }
  }, [isAr]);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    const num = parseFloat(draftPercent);
    if (isNaN(num) || num < 0 || num > 100) {
      toast.error(t('النسبة يجب أن تكون بين 0 و 100', 'Percentage must be 0–100'));
      return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/api/admin/tax-settings`,
        { tax_percent: num, notes: draftNotes },
        { headers: getAuthHeaders() });
      toast.success(t('✓ تم الحفظ', '✓ Saved'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحفظ', 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async () => {
    setToggling(true);
    try {
      await axios.patch(`${API_URL}/api/admin/tax-settings`,
        { enabled: !settings.enabled },
        { headers: getAuthHeaders() });
      toast.success(t('تم تحديث حالة التفعيل', 'Enable state updated'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل التحديث', 'Update failed'));
    } finally {
      setToggling(false);
    }
  };

  const syncTRN = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/tax-settings/sync-trn`, {},
        { headers: getAuthHeaders() });
      toast.success(t(`تم استيراد الرقم الضريبي: ${r.data.found_trn}`,
                       `TRN synced: ${r.data.found_trn}`));
      load();
    } catch (e) {
      const detail = e.response?.data?.detail || t('فشل الاستيراد', 'Sync failed');
      toast.error(detail);
    } finally {
      setSyncing(false);
    }
  };

  const isDirty = settings &&
    (String(draftPercent) !== String(settings.tax_percent ?? '') ||
     (draftNotes || '') !== (settings.notes || ''));

  return (
    <div className="min-h-screen bg-[#030303] relative">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
      </div>

      <DashboardHeader showBackButton backPath="/dashboard/finance/vat" />

      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'المالية', label_en: 'Finance', path: '/dashboard/finance' },
            { label_ar: 'الضريبة المضافة', label_en: 'VAT', path: '/dashboard/finance/vat' },
            { label_ar: 'التحكم بالضريبة', label_en: 'Tax Control', path: null },
          ]} />
        </div>
      </div>

      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div className={isAr ? 'text-right' : 'text-left'}>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              {t('التحكم بالضريبة', 'Tax Control')}
            </h1>
            <p className="text-white/50 text-sm max-w-2xl">
              {t('حدّد نسبة الضريبة وفعّل/عطّل تطبيقها. عند التفعيل تُحسب تلقائياً في نموذج إنشاء الفاتورة.',
                 'Set the tax rate and toggle its application. When enabled, the tax is auto-computed in every invoice.')}
            </p>
          </div>
          <Button onClick={load} variant="outline"
                  className="border-white/10 bg-transparent text-white/80 hover:bg-white/5"
                  data-testid="tax-refresh">
            <RefreshCw className={`w-4 h-4 me-1 ${loading ? 'animate-spin' : ''}`} />
            {t('تحديث', 'Refresh')}
          </Button>
        </div>

        {loading || !settings ? (
          <div className="p-16 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-white/40" />
          </div>
        ) : (
          <div className="space-y-5">
            {/* Enable / Disable */}
            <div className={`p-5 rounded-2xl border flex items-center justify-between gap-3 ${
              settings.enabled
                ? 'bg-emerald-500/5 border-emerald-500/20'
                : 'bg-white/[0.02] border-white/10'
            }`} data-testid="tax-enable-card">
              <div className="flex-1">
                <div className="text-white font-bold text-lg mb-1">
                  {t('تطبيق الضريبة', 'Apply tax')}
                </div>
                <div className={`text-xs ${settings.enabled ? 'text-emerald-400' : 'text-white/50'}`}>
                  {settings.enabled
                    ? t('مُفعّل — الضريبة تظهر في نموذج إنشاء الفاتورة بمعدل ' + settings.tax_percent + '%',
                         `Enabled — tax at ${settings.tax_percent}% is applied to invoices`)
                    : t('مُعطّل — لا يظهر حقل الضريبة في نموذج إنشاء الفاتورة',
                         'Disabled — tax field is hidden from the invoice form')}
                </div>
              </div>
              <button onClick={toggleEnabled} disabled={toggling}
                      className="p-1.5 rounded-md hover:bg-white/5 disabled:opacity-50"
                      data-testid="tax-toggle">
                {toggling ? (
                  <Loader2 className="w-6 h-6 animate-spin text-white/40" />
                ) : settings.enabled ? (
                  <ToggleRight className="w-11 h-11 text-emerald-400" />
                ) : (
                  <ToggleLeft className="w-11 h-11 text-white/30" />
                )}
              </button>
            </div>

            {/* Tax percentage + notes */}
            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5" data-testid="tax-config-card">
              <h3 className="text-white font-bold mb-4">{t('الإعدادات', 'Configuration')}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-white/70 text-xs mb-1.5">
                    {t('نسبة الضريبة %', 'Tax percentage %')}
                  </label>
                  <div className="relative w-full max-w-xs">
                    <Input type="number" step="0.01" min="0" max="100"
                           value={draftPercent}
                           onChange={(e) => setDraftPercent(e.target.value)}
                           className="bg-[#050505] border-white/10 text-white h-11 pe-8"
                           data-testid="tax-percent-input" />
                    <span className="absolute inset-y-0 end-3 flex items-center text-white/40 text-sm">%</span>
                  </div>
                  <p className="text-white/40 text-[11px] mt-1">
                    {t('القيمة الافتراضية في الإمارات: 5%', 'UAE default: 5%')}
                  </p>
                </div>

                <div>
                  <label className="block text-white/70 text-xs mb-1.5">
                    {t('ملاحظات', 'Notes')}
                  </label>
                  <Input value={draftNotes}
                         onChange={(e) => setDraftNotes(e.target.value)}
                         placeholder={t('اختياري', 'Optional')}
                         className="bg-[#050505] border-white/10 text-white h-11"
                         data-testid="tax-notes-input" />
                </div>

                <Button onClick={saveSettings} disabled={!isDirty || saving}
                        className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-black font-bold h-11 px-6 disabled:opacity-50"
                        data-testid="tax-save">
                  {saving
                    ? <><Loader2 className="w-4 h-4 animate-spin me-2" />{t('جارٍ الحفظ...', 'Saving...')}</>
                    : <><Save className="w-4 h-4 me-2" />{t('حفظ التغييرات', 'Save changes')}</>}
                </Button>
              </div>
            </div>

            {/* TRN */}
            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5" data-testid="tax-trn-card">
              <h3 className="text-white font-bold mb-2">
                {t('الرقم الضريبي (TRN)', 'Tax Registration Number (TRN)')}
              </h3>
              <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20 text-blue-200/80 text-xs mb-4 flex items-start gap-2">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p>
                  {t('يُستورَد الرقم الضريبي تلقائياً من "أوراق الشركة" (شهادة الضريبة). إذا كان الحقل فارغاً، تأكد من رفع الشهادة أولاً.',
                     'The TRN is auto-imported from Company Documents (Tax Certificate). If it\'s empty, upload the certificate first.')}
                </p>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-1 font-mono text-lg text-white bg-[#050505] border border-white/10 rounded-md h-11 px-4 flex items-center"
                     data-testid="tax-trn-value" dir="ltr">
                  {settings.trn_number || <span className="text-white/30">{t('غير مضبوط', 'Not set')}</span>}
                </div>
                <Button onClick={syncTRN} disabled={syncing}
                        className="bg-white/10 hover:bg-white/15 text-white h-11 border border-white/10"
                        data-testid="tax-sync-trn">
                  {syncing
                    ? <><Loader2 className="w-4 h-4 animate-spin me-2" />{t('جارٍ...', 'Syncing...')}</>
                    : <><RotateCw className="w-4 h-4 me-2" />{t('مزامنة من أوراق الشركة', 'Sync from Company Docs')}</>}
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
